<template>
  <!-- Loading State -->
  <div v-if="loading" class="loading-container">
    <v-skeleton-loader
      type="image, article, table"
      class="mb-6"
    ></v-skeleton-loader>
  </div>

  <div class="need-details-container" v-else>
    <!-- Loading State -->
    <v-skeleton-loader
      v-if="loading"
      type="article, article"
      class="loading-skeleton"
    />

    <!-- Age Group Warning -->
    <v-alert
      v-if="need && profileType === 'Volunteer' && isNotAgeGroup()"
      type="warning"
      class="mb-4"
      border="left"
      colored-border
      elevation="1"
    >
      <div class="d-flex align-center">
        <v-icon left>mdi-alert</v-icon>
        This opportunity is not compatible with your age group
      </div>
      <div class="mt-2">
        <div>
          <strong>Required age:</strong>
          <span v-if="need.min_age != 0 && need.max_age != 120 && need.max_age">
            {{ need.min_age }} to {{ need.max_age }} years old
          </span>
          <span v-else-if="need.min_age"> {{ need.min_age }} and up </span>
          <span v-else-if="need.max_age && need.max_age != 120">
            {{ need.max_age }} and younger
          </span>
        </div>
        <div><strong>Your age group:</strong> {{ profile.age_group }}</div>
      </div>
    </v-alert>

    <!-- Incomplete Need Warning -->
    <v-alert
      v-if="need && !schedules.length && !loadingShifts"
      type="error"
      class="mb-4"
      border="left"
      colored-border
      elevation="1"
    >
      <div class="d-flex align-center">
        <v-icon left>mdi-alert-circle</v-icon>
        The information registered in the need is incomplete.
      </div>
      <div
        v-if="profileType === 'Agency' && profile._id === need.agency"
        class="mt-2"
      >
        Please complete the "shift" field to allow volunteers to sign up.
      </div>
    </v-alert>

    <!-- Main Need Card -->

    <v-card v-if="need" class="mb-6 need-card" elevation="2">
      <v-card-title
        class="need-header d-flex flex-column flex-md-row justify-space-between align-start"
      >
        <!-- Left: Need Image + Title + Agency Info -->
        <div class="d-flex flex-column flex-md-row flex-grow-1">
          <!-- Need Image -->
          <div class="mr-4 need-image-container">
            <v-img
              :src="require('@/assets/svg/needs.svg')"
              :width="$vuetify.breakpoint.xs ? '60' : '80'"
              :height="$vuetify.breakpoint.xs ? '60' : '80'"
              contain
            />
          </div>

          <!-- Title and Meta Info -->
          <div class="need-info">
            No Token
            <h1 class="need-title">
              {{ need.title }}
            </h1>

            <!-- Agency Info -->
            <div class="d-flex align-center flex-wrap">
              <div class="agency-link" @click="goToAgencyProfile">
                <v-avatar small class="mr-1" v-if="need.agency_image">
                  <img :src="need.agency_image" alt="Agency Image" size="14" />
                </v-avatar>
                <v-icon small class="mr-1" v-else>mdi-account-group</v-icon>
                {{ need.agency_name }}
              </div>

              <!-- Status -->
              <div class="ml-2 duration-chip">
                <v-chip
                  small
                  color="purple lighten-5"
                  text-color="purple darken-2"
                >
                  <v-icon small left>mdi-clock</v-icon>
                  {{ need.duration }}
                </v-chip>
              </div>
            </div>

            <!-- Address -->
            <div class="d-flex align-center mt-1 need-location">
              <v-icon small class="mr-1">mdi-map-marker</v-icon>
              {{ need.address ? `${need.address}, ` : "" }}{{ need.city }},
              {{ need.state }} {{ need.zip }}
            </div>

            <!-- Dates -->
            <div class="d-flex align-center mt-1 need-date">
              <v-icon small class="mr-1">mdi-calendar</v-icon>

              {{ formatDate(need.start_date) }} to
              {{ formatDate(need.end_date) }}
            </div>
            <!-- Interest Icons -->
            <div class="d-flex flex-wrap mt-3 need-interests">
              <!-- <site-icon
                v-for="(icon, key) in need.interests"
                :key="key"
                :name="icon"
                type="Interest"
                class="mr-2 mb-2"
                :size="$vuetify.breakpoint.xs ? '30' : '40'"
              /> -->
            </div>
            <!-- Right: Buttons -->
            <div
              class="mt-3 mt-md-0 d-flex align-center justify-start flex-wrap"
            >
              <div v-if="need.agency === profile._id" class="mr-2">
                <v-btn color="primary" small outlined @click="onResponse">
                  <v-icon left small>mdi-account-group</v-icon>
                  Volunteer List
                </v-btn>
              </div>

              <div
                v-else-if="
                  profileType === 'Volunteer' || profileType === 'Company'
                "
                class="mr-2"
              >
                <v-btn
                  color="primary"
                  small
                  outlined
                  @click="goToDiary(profileType)"
                >
                  <v-icon left small>mdi-notebook</v-icon>
                  View Diary
                </v-btn>
              </div>

              <div v-if="workable" class="mr-2">
                <v-btn color="success" small @click="startWork">
                  <v-icon left small>mdi-play</v-icon>
                  Start Work
                </v-btn>
              </div>

            </div>           
          </div>
          
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-if="need.agency === profile._id"
                    @click="onEditNeed"
                  >
                    <v-list-item-title><v-icon small left color="#3D90D7">mdi-pencil</v-icon> Edit Need</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="need.agency === profile._id"
                    @click="onDeleteNeed"
                  >
                    <v-list-item-title><v-icon small left color="#E83F25">mdi-delete</v-icon> Delete Need</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="onShare">
                    <v-list-item-title><v-icon small left color="#3F7D58">mdi-share-variant</v-icon> Share</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
        </div>
      </v-card-title>

      <!-- Description and Map -->
      <v-card-text class="need-content">
        <section class="need-section">
          <h2 class="section-title">
            <v-icon color="primary" class="mr-2">mdi-text</v-icon>
            Description
          </h2>
          <div class="section-content text-block" v-linkified>
            {{ need.description }}
          </div>
        </section>

        <section class="need-section">
          <h2 class="section-title">
            <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
            Location
          </h2>
          <div class="section-content">
            <div class="mb-3">
              {{ need.address ? `${need.address}, ` : "" }}{{ need.city }},
              {{ need.state }} {{ need.zip }}
            </div>
            <GmapMap
              v-if="center"
              :center="center"
              :zoom="15"
              map-type-id="terrain"
              class="location-map"
            >
              <GmapMarker
                :position="center"
                :icon="require('@/assets/svg/needs.svg')"
              />
            </GmapMap>
          </div>
        </section>
      </v-card-text>
    </v-card>

    <!-- Main Need Card -->

    <!-- Shifts Section -->
    <v-card class="mb-6" elevation="2" v-if="need">
      <v-card-title class="d-flex justify-space-around align-center">
        <h2 class="headline">
          <v-icon left>mdi-calendar-clock</v-icon>
          Shifts Schedule
        </h2>
        <v-btn
          color="primary"
          small
          outlined
          @click="onResponse"
          class="shift-section-button"
          v-if="need.agency === profile._id"
        >
          <v-icon left small>mdi-account-group</v-icon>
          Volunteer List
        </v-btn>
        <v-btn
          v-if="need.agency === profile._id"
          color="primary"
          small
          @click="onAddShift"
          class="ms-auto"
        >
          <v-icon left small>mdi-plus</v-icon>
          Add Shift
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="schedules"
          :loading="loadingShifts"
          loading-text="Loading shifts... Please wait"
          no-data-text="No shifts available"
          class="elevation-1 shifts-table"
          @click:row="goToShift"
        >
          <!-- Title Column -->
          <template v-slot:item.title="{ item }">
            <div class="shift-title">
              <v-icon small class="mr-2">mdi-calendar-text</v-icon>
              {{ item.title || `Shift ${schedules.indexOf(item) + 1}` }}
            </div>
          </template>

          <!-- Date Column -->
          <template v-slot:item.begins="{ item }">
            <div class="date-cell">
              <div class="date-display">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                <span class="date-text">
                  {{ formatFullDate(item.start_date) }}
                </span>
              </div>
              <!-- <div v-if="item.end_date" class="date-range">
                <span class="date-separator">(to)</span>
                <span class="date-text">
                  {{ formatFullDate(item.end_date) }}
                </span>
              </div> -->
              <div class="time-display">
                <v-icon small class="mr-1">mdi-clock</v-icon>
                {{ formatTimeRange(item.start_date, item.hours) }}
              </div>
            </div>
          </template>

          <!-- Duration Column -->
          <template v-slot:item.hours="{ item }">
            <v-chip small color="blue lighten-5" text-color="blue darken-2">
              {{ item.hours }} hours
              <span v-if="item.hours_description">
                ({{ item.hours_description }})
              </span>
            </v-chip>
          </template>

          <!-- Capacity Column -->
          <template v-slot:item.capacity="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-progress-circular
                  :value="calculateCapacityPercentage(item)"
                  :size="55" 
                  :width="5" 
                  :color="getCapacityColor(item)"
                  v-on="on"
                >
                  <v-icon small v-if="calculateCapacityPercentage(item) >= 100">mdi-alert</v-icon>
                  <span v-else class="font-weight-bold"> 
                    {{ calculateAvailableSpots(item) }}
                  </span>
                </v-progress-circular>
              </template>
              <span class="font-weight-bold">  <!-- Bold tooltip -->
                {{ calculateAvailableSpots(item) }}/{{ item.capacity }} available
              </span>
            </v-tooltip>
          </template>

          <!-- Status Column (for volunteers) -->
            <template v-slot:item.status="{ item }">
              <div class="status-indicator" v-if="getResponseStatus(item)">
                <span 
                  class="status-text" 
                  :style="{ color: getStatusColor(getResponseStatus(item)) }"
                >
                  {{ getResponseStatus(item) }}
                </span>
                <v-icon 
                  small 
                  :color="getStatusColor(getResponseStatus(item))"
                  class="status-icon"
                >
                  {{ getStatusIcon(getResponseStatus(item)) }}
                </v-icon>
              </div>
            </template>





          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
              <!-- Agency Actions -->
              <template v-if="need.agency === profile._id">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop="onScanQR(item)"
                      class="mr-1"
                    >
                      <v-icon small>mdi-qrcode-scan</v-icon>
                    </v-btn>
                  </template>
                  <span>Scan QR</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop="onDeleteShift(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Shift</span>
                </v-tooltip>
              </template>

              <!-- Volunteer/Company Actions -->
              <template v-else>
               <v-btn
                  v-if="checkMyResponse(item)"
                  color="primary"
                  small
                  outlined
                  @click.stop="onRepondShift(item)"
                  class="mr-2 view-btn"
                  :loading="item.loading"
                >
                  <v-icon left>mdi-eye</v-icon>
                  View
                </v-btn>


               <v-btn
                  v-if="checkConfirmedResponse(item)"
                  color="success"
                  small
                  outlined
                  @click.stop="startWork(item)"
                  class="mr-2 start-btn"
                  :loading="item.loading"
                >
                  <v-icon left>mdi mdi-play-circle</v-icon>
                  Begin
                </v-btn>


              <v-btn
                v-else-if="item.responses.length === item.capacity"
                color="grey"
                small
                disabled
                class="mr-2"
                outlined
              >
                <v-icon left>mdi-close-circle</v-icon>
                Full
              </v-btn>
                <v-btn
                  v-else-if="checkSubmittedResponse(item) && new Date(item.start_date) >= new Date()"
                  color="primary"
                  small
                  outlined
                  @click.stop="onRepondShift(item)"
                  class="mr-2 join-btn"
                >
                  <v-icon left>mdi-handshake</v-icon>
                  Join
                </v-btn>

                              <v-btn
                  v-else-if="new Date(item.start_date) < new Date()"
                  color="grey"
                  small
                  disabled
                  class="mr-2"
                >
                  <v-icon left>mdi-calendar-remove</v-icon>
                  Ended
                </v-btn>

              </template>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Additional Details Section -->
    <v-card
      v-if="need && hasAdditionalDetails()"
      class="mb-6 details-card"
      elevation="2"
    >
      <v-card-title class="details-header">
        <h2 class="section-title">
          <v-icon color="primary" class="mr-2">mdi-information</v-icon>
          Additional Details
        </h2>
      </v-card-title>

      <v-card-text>
        <v-list dense class="details-list">
          <!-- Age Requirements -->
          <v-list-item
            v-if="need.min_age != 0 || (need.max_age && need.max_age != 120)"
          >
            <v-list-item-icon>
              <v-icon color="primary">mdi-account</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Age Requirements</v-list-item-title>
              <v-list-item-subtitle>
                <span
                  v-if="
                    need.min_age != 0 && need.max_age != 120 && need.max_age
                  "
                >
                  {{ need.min_age }} to {{ need.max_age }} years old
                </span>
                <span v-else-if="need.min_age">
                  {{ need.min_age }} and up
                </span>
                <span v-else-if="need.max_age && need.max_age != 120">
                  {{ need.max_age }} and younger
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Family Friendly -->
          <v-list-item
            v-if="need.family_friendly && need.family_friendly != 'N/A'"
          >
            <v-list-item-icon>
              <v-icon color="primary">mdi-human-male-female-child</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Family Friendly</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.family_friendly === "Yes" ? "Yes" : "No" }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Outdoors -->
          <v-list-item v-if="need.outdoors && need.outdoors != 'N/A'">
            <v-list-item-icon>
              <v-icon color="primary">mdi-weather-sunny</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Outdoors</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.outdoors === "Yes" ? "Yes" : "No" }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Wheelchair Accessible -->
          <v-list-item
            v-if="
              need.wheelchair_accessible && need.wheelchair_accessible != 'N/A'
            "
          >
            <v-list-item-icon>
              <v-icon color="primary">mdi-wheelchair-accessibility</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Wheelchair Accessible</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.wheelchair_accessible === "Yes" ? "Yes" : "No" }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Additional Attributes -->
          <v-list-item v-if="need.attributes && need.attributes.length != 0">
            <v-list-item-icon>
              <v-icon color="primary">mdi-tag-multiple</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Attributes</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.attributes.join(", ") }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Images Section -->
    <v-card
      v-if="need && need.images && need.images.length"
      class="mb-6 images-card"
      elevation="2"
    >
      <v-card-title class="images-header">
        <h2 class="section-title">
          <v-icon color="primary" class="mr-2">mdi-image-multiple</v-icon>
          Images
        </h2>
      </v-card-title>

      <v-card-text>
        <need-photo-viewer :image-data="need.images" :editable="false" />
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-card v-if="!need && error" class="error-card">
      <v-card-title class="justify-center py-10">
        <v-icon color="error" large class="mr-3">mdi-alert-circle</v-icon>
        {{ error }}
      </v-card-title>
      <v-card-actions class="justify-center pb-8">
        <v-btn color="primary" @click="$router.go(-1)">
          Go Back
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialogs -->
    <agency-edit-need
      :item="need"
      :dialog="editDialog"
      :onCloseDialog="onCloseEditDialog"
      v-if="editDialog && need"
    />

    <confirm-delete-need-dialog
      :deleteDialog="deleteDialog"
      :need="need"
      :onClose="onCloseConfirmDelete"
      :onDeleted="onDeletedNeed"
    />

    <confirm-delete-shift-dialog
      :deleteDialog="deleteShiftDialog"
      :shift="selectedShift"
      :onClose="onCloseConfirmDelete"
      :onDeleted="onShiftDeleted"
    />


    <response-shift
      :dialog="responseDialog"
      :shift="selectedShift"
      :need="need"
      :onCloseDialog="onCloseResponseDialog"
      :onResponse="onRespondedShift"
      :onCancelled="onCancelledShift"
    />

    <volunteer-time-tracker
      :dialog="trackerDialog"
      :onCloseDialog="onCloseTrackerDialog"
      :need_id="need?._id"
      v-if="profileType === 'Volunteer' || (profileType === 'Company' && need)"
    />

    <shift-qr-dialog
      :dialog="qrDialog"
      :shift="selectedShift"
      :onCloseDialog="onCloseQRDialog"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="3000" color="success">
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import ConfirmDeleteNeedDialog from "@/components/common/ConfirmDeleteNeedDialog.vue";
import AgencyEditNeed from "./create/AgencyEditNeed.vue";
import dateFormat from "dateformat";
import ConfirmDeleteShiftDialog from "@/components/common/ConfirmDeleteShiftDialog.vue";
import ResponseShift from "../../volunteer/response/ResponseShift.vue";
import VolunteerTimeTracker from "../../volunteer/response/VolunteerTimeTracker.vue";
// import SiteIcon from "@/components/common/SiteIcon.vue";
import NeedPhotoViewer from "../../../components/agency/NeedPhotoViewer.vue";
import ShiftQrDialog from "./shift/ShiftQrDialog.vue";
export default {
  components: {
    ConfirmDeleteNeedDialog,
    AgencyEditNeed,
    ConfirmDeleteShiftDialog,
    ResponseShift,
    VolunteerTimeTracker,
    // SiteIcon,
    NeedPhotoViewer,
    ShiftQrDialog
  },
  data() {
    return {
      loading: false,
      loadingShifts: false,
      need: null,
      center: null,
      schedules: [],
      headers: [
        { text: "Title", value: "title" },
        { text: "Date", value: "begins" },
        { text: "Duration", value: "hours" },
        { text: "Open spots", value: "capacity" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      deleteDialog: false,
      qrDialog: false,
      snackbar: false,
      snackMessage: null,
      editDialog: false,
      selectedShift: null,
      createShiftDialog: false,
      deleteShiftDialog: false,
      editShiftDialog: false,
      responseDialog: false,
      workable: false,
      trackerDialog: false,
      error: null
    };
  },
  computed: {
    ...mapGetters({
      profile: "auth/getProfile",
      profileType: "auth/getType",
      timezone: "usersetting/getTimeZone"
    })
  },
  methods: {
    ...mapActions("need", {
      getNeedDetails: "getUnAuthNeedDetails",
      getLocation: "getLocation",
      fetchAllShifts: "fetchAllUnAuthShifts",
      deleteShift: "deleteShift",
      cancelResponse: "cancelResponse"
    }),
 getStatusIcon(status) {
    switch (status) {
      case 'Confirmed':
        return 'mdi-check-circle';
      case 'Pending':
        return 'mdi-clock-outline';
      case 'Rejected':
        return 'mdi-close-circle';
      default:
        return 'mdi-help-circle';
    }
  },
    async fetchNeedDetails() {
      this.loading = true;
      try {
        this.need = await this.getNeedDetails({ _id: this.$route.query._id });
        if (this.need) {
          this.getLocationFromAddress();
          this.getAllShifts();
        } else {
          this.error = "Need not found";
        }
      } catch (error) {
        console.error("Error loading need:", error);
        this.error = "Failed to load need details";
      } finally {
        this.loading = false;
      }
    },

    isAgencyOwner() {
      return this.need && this.profile && this.need.agency === this.profile._id;
    },
    getLocationFromAddress() {
      this.center = { lat: this.need.latitude, lng: this.need.longitude };
    },
    getAllShifts() {
      this.loadingShifts = true;
      this.fetchAllShifts({ need_id: this.$route.query._id })
        .then(res => {
          this.loadingShifts = false;
          this.schedules = res.reverse();
          this.workable = false;
          for (var i = 0; i < this.schedules.length; i++) {
            for (var j = 0; j < this.schedules[i].responses.length; j++) {
              if (
                this.schedules[i].responses[j].user_id == this.profile._id &&
                this.schedules[i].responses[j].status == "Confirmed"
              )
                this.workable = true;
              break;
            }
            if (this.workable) break;
          }
        })
        .catch(error => {
          console.log(error.response.data.message);
          this.loadingShifts = false;
        });
    },
    // Enhanced date formatting methods
    formatFullDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    formatTimeRange(startDate, hours) {
      if (!startDate) return "";
      const start = new Date(startDate);
      const end = new Date(start.getTime() + hours * 60 * 60 * 1000);

      return `${start.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      })} - ${end.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      })}`;
    },

    calculateCapacityPercentage(item) {
      if (item.capacity === 0) return 0;
      return (
        ((item.capacity - this.calculateAvailableSpots(item)) / item.capacity) *
        100
      );
    },

    calculateAvailableSpots(item) {
      return (
        item.capacity -
        (item.responses
          ? item.responses.filter(r => r.status !== "Rejected").length
          : 0)
      );
    },

    getCapacityColor(item) {
      if (item.capacity === 0) return "grey";
      const percentage = this.calculateCapacityPercentage(item) / 1000;

      if (percentage === 0) return "red";
      if (percentage < 0.25) return "orange";
      return "green";
    },

    getStatusColor(status) {
      switch (status) {
        case "Confirmed":
          return "#3F7D58";
        case "Pending":
          return "#F0A04B";
        case "Rejected":
          return "#CB0404";
        default:
          return "#1B56FD";
      }
    },
    hasAdditionalDetails() {
      if (!this.need) return false;
      return (
        this.need.min_age != 0 ||
        this.need.max_age != 120 ||
        (this.need.family_friendly && this.need.family_friendly != "N/A") ||
        (this.need.outdoors && this.need.outdoors != "N/A") ||
        (this.need.wheelchair_accessible &&
          this.need.wheelchair_accessible != "N/A") ||
        (this.need.attributes && this.need.attributes.length != 0)
      );
    },
    formatDate(date) {
      if (!date) return "";
      // Parse the ISO date string (e.g., "2025-05-31T00:00:00.000+00:00")
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    },

    // give only time in hh:MM TT format
    formatTime(date) {
      return dateFormat(this.convertTZ(date, this.timezone), "hh:MM TT");
    },
    convertTZ(date, tzString) {
      if (!date) return "";
      if (!tzString) {
        tzString = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString
          }
        )
      );
    },

    ///Volunteer
    isNotAgeGroup() {
      if (
        this.need.min_age == 0 &&
        (this.need.max_age || this.need.max_age == 120)
      )
        return false;

      if (!this.profile.age_group) return false;

      let my_min_age = 0;
      let my_max_age = 120;

      switch (this.profile.age_group) {
        case "13-18":
          my_min_age = 13;
          my_max_age = 18;
          break;
        case "19-24":
          my_min_age = 19;
          my_max_age = 24;
          break;
        case "25-34":
          my_min_age = 25;
          my_max_age = 34;
          break;
        case "35-44":
          my_min_age = 35;
          my_max_age = 44;
          break;
        case "45-54":
          my_min_age = 45;
          my_max_age = 54;
          break;
        case "55-64":
          my_min_age = 55;
          my_max_age = 64;
          break;
        case "65 and older":
          my_min_age = 65;
          my_max_age = 120;
          break;
      }

      if (my_max_age < this.need.min_age || my_min_age > this.need.max_age)
        return true;

      return false;
    },
    ///Agency
    onEditNeed() {
      this.editDialog = true;
    },
    onCloseEditDialog() {
      this.editDialog = false;
      this.getNeedDetails({ _id: this.$route.query._id }).then(need => {
        this.loading = false;
        this.need = need;
        this.getLocationFromAddress();
      });
    },
    onCloseCreateShiftDialog() {
      this.createShiftDialog = false;
    },
    onAddShift() {
      this.createShiftDialog = true;
    },
    onCreatedShift(shift) {
      this.createShiftDialog = false;
      this.schedules.unshift(shift);
      this.$refs.createShiftForm.reset();
    },
    onDeleteNeed(need) {
      this.selected = need;
      this.deleteDialog = true;
    },
    onDeletedNeed() {
      this.deleteDialog = false;
      this.$router.back();
    },
    onCloseConfirmDelete() {
      this.deleteDialog = false;
      this.deleteShiftDialog = false;
    },
    onEditShift(shift) {
      this.selectedShift = shift;
      this.editShiftDialog = true;
    },
    onScanQR(item) {
      this.selectedShift = item;
      this.qrDialog = true;
    },
    onDeleteShift(shift) {
      this.selectedShift = shift;
      this.deleteShiftDialog = true;
    },
    onCloseEditShiftDialog() {
      this.editShiftDialog = false;
    },
    onSavedShift(shift) {
      this.editShiftDialog = false;
      this.schedules = this.schedules.map(s => {
        if (s._id != shift._id) {
          return s;
        } else {
          return shift;
        }
      });
    },
    onShiftDeleted(shift) {
      this.schedules = this.schedules.filter(s => s._id != shift._id);
      this.deleteShiftDialog = false;
    },
    goToShift(event, { item }) {
      if (this.profile._id == this.need.agency) {
        this.$router.push({
          name: "agency-responses-feeds",
          query: { _id: this.need._id, selected_shift: item._id }
        });
      }
    },
    onResponse() {
      this.$router.push({
        name: "agency-responses-feeds",
        query: { _id: this.need._id }
      });
    },
    ///Volunteer
    onRepondShift(shift) {
      this.selectedShift = shift;
      this.responseDialog = true;
    },
    onRespondedShift(response) {
      this.responseDialog = false;
      this.schedules = this.schedules.map(shift => {
        if (shift._id == response.shift_id) {
          shift.responses = shift.responses.filter(r => r._id != response._id);
          shift.responses.push(response);
        }
        return shift;
      });
    },
    onCancelledShift(response) {
      this.responseDialog = false;
      this.schedules = this.schedules.map(shift => {
        if (shift._id == response.shift_id) {
          shift.responses = shift.responses.filter(r => r._id != response._id);
        }
        return shift;
      });
    },
    onCloseResponseDialog() {
      this.responseDialog = false;
    },
    checkMyResponse(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (shift.responses[i].user_id == this.profile._id) return true;
      }
      return false;
    },
    checkConfirmedResponse(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (
          shift.responses[i].user_id == this.profile._id &&
          shift.responses[i].status == "Confirmed"
        )
          return true;
      }
      return false;
    },
    checkSubmittedResponse(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (shift.responses[i].user_id == this.profile._id) return false;
      }
      return true;
    },
    getResponseStatus(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (
          shift.responses[i].user_id == this.profile._id &&
          shift.responses[i].status
        )
          return shift.responses[i].status;
      }
      return "";
    },
    startWork() {
      this.trackerDialog = true;
    },
    onCloseTrackerDialog() {
      this.trackerDialog = false;
    },
    onCloseQRDialog() {
      this.qrDialog = false;
    },
    onCancelRespond(shift) {
      this.schedules = this.schedules.map(s =>
        s._id == shift._id ? { ...s, loading: true } : s
      );
      this.cancelResponse({ _id: shift._id })
        .then(res => {
          shift.responses = shift.responses.filter(r => r._id != res._id);
          this.schedules = this.schedules.map(s =>
            s._id == shift._id ? shift : s
          );
        })
        .catch(error => {
          console.log(error);
        });
    },
    async onShare() {
      this.copyToClipboard(
        process.env.VUE_APP_WEB_URL + "/needs/details?_id=" + this.need._id
      );
      this.snackbar = true;
      this.snackMessage = "Copied the URL";
    },
    copyToClipboard(textToCopy) {
      // navigator clipboard api needs a secure context (https)
      if (navigator.clipboard && window.isSecureContext) {
        // navigator clipboard api method'
        return navigator.clipboard.writeText(textToCopy);
      } else {
        // text area method
        let textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        // make the textarea out of viewport
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        return new Promise((res, rej) => {
          // here the magic happens
          document.execCommand("copy") ? res() : rej();
          textArea.remove();
        });
      }
    },
    goToDiary(profile) {
      if (profile === "Volunteer") {
        this.$router.push({
          name: "volunteer-diary",
          query: {
            need_id: this.need._id
          }
        });
      } else if (profile === "Company") {
        this.$router.push({
          name: "company-diary",
          query: {
            need_id: this.need._id
          }
        });
      }
    },
    goToAgencyProfile() {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: this.need.agency }
      });
    }
  },
  mounted() {
    if (!this.$route.query._id) {
      this.$router.push({ name: "agency-need-feeds" });
      return;
    }
    if (this.profileType == "Volunteer") {
      this.headers = [
        { text: "Title", value: "title" },
        { text: "Date", value: "begins" },
        { text: "Duration", value: "hours" },
        { text: "Open spots", value: "capacity" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false }
      ];
    }
    if (this.profileType == "Company") {
      this.headers = [
        { text: "Title", value: "title" },
        { text: "Date", value: "begins" },
        { text: "Duration", value: "hours" },
        { text: "Open spots", value: "capacity" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false }
      ];
    }
    this.loading = true;
    this.getNeedDetails({ _id: this.$route.query._id })
      .then(need => {
        this.loading = false;
        this.need = need;
        this.getLocationFromAddress();
        this.getAllShifts();
      })
      .catch(error => {
        this.loading = false;
        if (error.response != undefined && error.response.status === 401) {
          this.tokenDialog = true;
        } else {
          console.log(error.response.data.message);
          if (error.response.data.message == "Need not found") {
            this.error = "Deleted or invalid need";
          }
        }
      });
  },
  watch: {
    profileType(newValue) {
      if (newValue == "Volunteer") {
        this.headers = [
          { text: "Title", value: "title" },
          { text: "Date", value: "begins" },
          { text: "Duration", value: "hours" },
          { text: "Open spots", value: "capacity" },
          { text: "Status", value: "status" },
          { text: "Actions", value: "actions", sortable: false }
        ];
      }
      if (newValue == "Company") {
        this.headers = [
          { text: "Title", value: "title" },
          { text: "Date", value: "begins" },
          { text: "Duration", value: "hours" },
          { text: "Open spots", value: "capacity" },
          { text: "Status", value: "status" },
          { text: "Actions", value: "actions", sortable: false }
        ];
      }
    }
  }
};
</script>
<style scoped>
.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.status-icon {
  margin-right: 4px;
}

.status-text {
  font-size: 0.8125rem;
  font-weight: 500;
  text-transform: capitalize;
}
/* Container Styles */
.need-details-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* Card Styles */
.need-card,
.shifts-card,
.details-card,
.images-card {
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.need-card:hover,
.shifts-card:hover,
.details-card:hover,
.images-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1) !important;
}

/* Header Styles */
.need-header,
.shifts-header,
.details-header,
.images-header {
  padding: 20px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.need-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  word-break: break-word;
}

/* Content Styles */
.need-content {
  padding: 24px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 24px;
}

/* Need Image */
.need-image-container {
  flex-shrink: 0;
}

.need-image {
  border-radius: 8px;
}

/* Need Meta Info */
.need-meta {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.agency-link {
  color: #1976d2;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.agency-link:hover {
  text-decoration: underline;
}

.duration-chip {
  margin-left: 8px;
}

.need-location {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 4px;
}
.need-date {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 4px;
}
.need-interests {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

/* Action Buttons */
.need-actions {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.action-buttons {
  display: flex;
  align-items: center;
  padding: 0px 40px;
}

.more-actions {
  border: 1px solid #ddd;
}

/* Map Styles */
.location-map {
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

/* Table Styles */
.shifts-table {
  border-radius: 8px;
  overflow: hidden;
}

.shifts-table >>> .v-data-table-header {
  background-color: #f8f9fa;
}

.shift-title {
  font-weight: 500;
}

.shift-date,
.shift-time {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.shift-time {
  color: #6c757d;
}

.capacity-bar {
  min-width: 100px;
}

/* Details List */
.details-list {
  background-color: transparent !important;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .need-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .need-actions {
    margin-top: 16px;
    align-items: flex-start;
    width: 100%;
  }

  .action-buttons {
    margin-top: 8px;
  }
}

@media (max-width: 600px) {
  .need-details-container {
    padding: 12px;
  }

  .need-title {
    font-size: 1.3rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .shifts-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .shifts-header > div {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-end;
  }
}

/* Text Block */
.text-block {
  white-space: pre-line;
}

/* Error Card */
.error-card {
  text-align: center;
  border-radius: 12px;
}

.error-card .v-card__title {
  color: #dc3545;
  font-weight: 500;
}
/* Loading Styles */
.loading-container {
  padding: 20px;
}

/* Table Styles */
.shifts-table {
  border-radius: 8px;
  overflow: hidden;
}

.shifts-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

/* Date Cell Styles */
.date-cell {
  min-width: 180px;
}

.date-display {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.date-range {
  display: flex;
  align-items: center;
  margin: 4px 0;
}

.date-separator {
  font-size: 0.8em;
  color: #666;
  margin: 0 2px;
}

.shift-section-button {
  margin: 0px 0px 0px 108px;
}

.date-text {
  font-weight: 500;
}

.time-display {
  display: flex;
  align-items: center;
  font-size: 0.9em;
  color: #666;
}

/* Shift Title */
.shift-title {
  display: flex;
  align-items: center;
  font-weight: 500;
}

/* Capacity Bar */
.capacity-container {
  min-width: 120px;
}

/* Actions Container */
.actions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .shifts-table {
    overflow-x: auto;
  }

  .date-cell {
    min-width: 150px;
  }
}

@media (min-width: 960px) {
  .v-application .ml-md-auto {
    margin-left: 280px !important;
  }
}

.view-btn{
  color:#1976d2 !important;
  border: 1px solid #1976d2;
  border-style:dashed;
  border-radius: 24px;
}

.join-btn{
  color:#1976d2 !important;
  border: 1px solid #1976d2;
  border-style:dashed;
  border-radius: 24px;
}

.start-btn{
  color:#4caf50 !important;
  border: 1px solid #4caf50;
  border-style:dashed;
  border-radius: 24px;
}
</style>
